import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

export default function Home({ me }) {
  const [sessions, setSessions] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, v] = await Promise.all([api.listSessions(), api.listVideos()]);
        setSessions(s.slice(0, 3));
        setVideos(v.slice(0, 6));
      } catch {}
    })();
  }, []);

  const first = me?.email?.split('@')[0];

  return (
    <div className="max-w-[1000px] mx-auto p-4 md:p-6">
      {/* Hero */}
      <div className="py-10 md:py-16 text-center">
        <div className="inline-block mb-4 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-cream-300 border border-fairway-700 rounded-full">
          Ekko
        </div>
        <h1 className="display text-4xl md:text-6xl text-cream-100 leading-[1.05] mb-3">
          Every swing <br className="hidden md:block" />
          <em className="text-cream-300">echoes the last.</em>
        </h1>
        <p className="text-ink-300 text-sm md:text-base max-w-xl mx-auto">
          Two swings, side by side. Frame-accurate scrubbing, drawing tools, sync to impact.
        </p>
        <div className="flex gap-3 justify-center mt-6">
          <Link
            to="/analyze"
            className="px-5 py-2.5 bg-cream-100 text-fairway-950 rounded font-medium text-sm hover:bg-cream-200 transition"
          >
            New comparison →
          </Link>
          <Link
            to="/library"
            className="px-5 py-2.5 border border-fairway-700 text-ink-100 rounded text-sm hover:bg-fairway-900 transition"
          >
            Upload video
          </Link>
        </div>
      </div>

      {/* Recent sessions */}
      {sessions.length > 0 && (
        <section className="mt-8">
          <div className="flex justify-between items-baseline mb-3">
            <h2 className="display text-lg text-cream-100">Recent sessions</h2>
            <Link to="/sessions" className="text-[11px] uppercase tracking-wider text-ink-400 hover:text-ink-100 transition">All →</Link>
          </div>
          <div className="space-y-2">
            {sessions.map(s => (
              <Link
                key={s.id}
                to={`/analyze/${s.id}`}
                className="flex items-center justify-between p-3 bg-fairway-900 border border-fairway-800 rounded-lg hover:border-fairway-600 transition"
              >
                <div>
                  <div className="text-sm text-cream-100">{s.name}</div>
                  <div className="text-[11px] text-ink-400 monospace">{new Date(s.updated_at).toLocaleString()}</div>
                </div>
                <span className="text-ink-400 text-lg">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recent videos */}
      {videos.length > 0 && (
        <section className="mt-8">
          <div className="flex justify-between items-baseline mb-3">
            <h2 className="display text-lg text-cream-100">Your library</h2>
            <Link to="/library" className="text-[11px] uppercase tracking-wider text-ink-400 hover:text-ink-100 transition">All →</Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {videos.map(v => (
              <Link
                key={v.id}
                to={`/analyze?a=${encodeURIComponent(v.r2_key)}`}
                className="block bg-fairway-900 border border-fairway-800 rounded overflow-hidden hover:border-cream-300 transition"
              >
                <div className="aspect-[9/16] bg-black">
                  <video src={api.streamUrl(v.r2_key)} preload="metadata" muted className="w-full h-full object-contain" />
                </div>
                <div className="p-1.5">
                  <div className="text-[10px] text-cream-100 truncate">{v.label || v.original_filename}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {sessions.length === 0 && videos.length === 0 && (
        <section className="mt-8 text-center py-12 border border-dashed border-fairway-700 rounded-lg">
          <p className="display text-xl text-ink-200 mb-2">Welcome{first ? `, ${first}` : ''}.</p>
          <p className="text-sm text-ink-400 max-w-md mx-auto">
            Upload two videos to compare them side by side. Start with a video of your own swing, then add another to compare against — past swing, friend's swing, or a pro from the library.
          </p>
        </section>
      )}
    </div>
  );
}
